import { api } from '../api.js';
import { createPaginationNavigationGuard } from '../pagination-navigation.js';
import { navigate } from '../router.js';
import { esc } from '../../../../src/shared/escape-html.js';
import { formatDateTime as fmtDate } from '../../../../src/shared/format-time.js';

function statusBadge(status) {
  const cls = { new: 'contact-status-new', reviewed: 'contact-status-read', resolved: 'contact-status-resolved', dismissed: 'contact-status-resolved' };
  return `<span class="${cls[status] || ''}">${status}</span>`;
}

const REASON_LABELS = {
  harassment: 'Harassment', hate_speech: 'Hate Speech', threats: 'Threats',
  cheating: 'Cheating', spam: 'Spam', other: 'Other',
};

let _listBound = false;
let _cursor = null;
let _nextCursor = null;
let _cursorHistory = [];
let _page = 1;
let _loadGen = 0;
const _navigation = createPaginationNavigationGuard();
let _activeStatus = '';
const PAGE_SIZE = 50;

function resetPagination() {
  _navigation.reset();
  _cursor = null;
  _nextCursor = null;
  _cursorHistory = [];
  _page = 1;
}

async function loadReportList(status = '') {
  const gen = ++_loadGen;
  _activeStatus = status;
  const body = document.getElementById('admin-reports-body');
  const statusEl = document.getElementById('admin-reports-status');
  if (!body) return;

  statusEl.textContent = 'Loading...';
  body.innerHTML = '';

  try {
    const params = new URLSearchParams({ limit: PAGE_SIZE });
    if (status) params.set('status', status);
    if (_cursor) params.set('cursor', _cursor);
    const data = await api.get(`/api/admin/reports?${params}`);
    if (gen !== _loadGen) return 'stale';
    const rows = data.reports;
    _nextCursor = data.next_cursor;
    document.getElementById('reports-prev').disabled = _cursorHistory.length === 0;
    document.getElementById('reports-next').disabled = !data.has_more;
    document.getElementById('reports-page-info').textContent = `Page ${_page}`;

    if (!rows.length) {
      statusEl.textContent = 'No reports found.';
      return true;
    }
    statusEl.textContent = '';

    body.innerHTML = rows.map(r => `
      <tr class="contact-row-clickable" data-id="${esc(r.id)}">
        <td style="white-space:nowrap">${esc(fmtDate(r.created_at))}</td>
        <td>${esc(r.reporter_username || '?')}</td>
        <td>${esc(r.reported_username || '?')}</td>
        <td>${esc(REASON_LABELS[r.reason] || r.reason)}</td>
        <td>${statusBadge(r.status)}</td>
        <td>${r.screenshot_count ? `${r.screenshot_count} img` : ''}</td>
      </tr>`).join('');

    body.querySelectorAll('.contact-row-clickable').forEach(tr => {
      tr.addEventListener('click', () => {
        navigate(`/admin/reports/${tr.dataset.id}`);
      });
    });
    return true;
  } catch {
    if (gen !== _loadGen) return 'stale';
    statusEl.textContent = 'Failed to load reports.';
    return false;
  }
}

async function loadReportDetail(id) {
  const content = document.getElementById('admin-reports-detail-content');
  if (!content) return;
  content.innerHTML = '<div class="admin-status">Loading...</div>';

  try {
    const r = await api.get(`/api/admin/reports/${id}`);

    const chatHtml = (r.chat_transcript || []).length ? `
      <div class="card admin-card">
        <div class="card-label">CHAT TRANSCRIPT</div>
        <div class="report-transcript">${(r.chat_transcript || []).map((m) => {
          const selected = Boolean(r.dm_message_id && m.flagged && m.messageId === r.dm_message_id);
          return `<div class="report-transcript-msg${selected ? ' report-transcript-msg-flagged' : ''}">${selected ? '<span class="report-transcript-flagged-label">FLAGGED</span> ' : ''}<span class="report-transcript-user">${esc(m.username || 'system')}:</span> ${esc(m.text)}</div>`;
        }).join('')}</div>
      </div>` : '';

    const voiceHtml = (r.voice_transcript || []).length ? `
      <div class="card admin-card">
        <div class="card-label">VOICE TRANSCRIPT (STT)</div>
        <div class="report-transcript">${(r.voice_transcript || []).map(m =>
          `<div class="report-transcript-msg">${esc(m.text)}</div>`
        ).join('')}</div>
      </div>` : '';

    const screenshots = r.screenshots || [];
    const screenshotHtml = screenshots.length ? `
      <div class="card admin-card">
        <div class="card-label">SCREENSHOTS</div>
        <div class="contact-detail-screenshots">
          ${screenshots.map(s => `<a href="/api/admin/reports/${esc(id)}/screenshot/${esc(s.name)}" target="_blank"><img src="/api/admin/reports/${esc(id)}/screenshot/${esc(s.name)}" alt="${esc(s.original_name || s.name)}" loading="lazy"></a>`).join('')}
        </div>
      </div>` : '';

    content.innerHTML = `
      <div class="card admin-card">
        <div class="contact-detail-meta">
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Reporter</span><span>${esc(r.reporter_username || r.reporter_id || 'Deleted account')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Reported</span><span style="color:#f87171;font-weight:bold">${esc(r.reported_username || r.reported_id || 'Deleted account')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Reason</span><span>${esc(REASON_LABELS[r.reason] || r.reason)}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Date</span><span>${esc(fmtDate(r.created_at))}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Room</span><span>${esc(r.room_code || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Status</span>${statusBadge(r.status)}${r.review_action ? ' · ' + esc(r.review_action) : ''}</div>
        </div>
        ${r.details ? `<div class="contact-detail-body">${esc(r.details)}</div>` : ''}
      </div>
      ${chatHtml}
      ${voiceHtml}
      ${r.clip_evidence ? `<div class="card admin-card"><div class="card-label">REPORTED CLIP</div>
        <p>Publication revision ${esc(r.clip_evidence.publicationRevision)}. The saved video includes commentary.</p>
        <p class="contact-detail-body">Reported caption: ${esc(r.clip_evidence.caption || '(No caption)')}</p>
        ${r.clip_video_expires_at && new Date(r.clip_video_expires_at).getTime() <= Date.now()
          ? '<p>The video retention period has ended. The reported caption remains in this report.</p>'
          : `<video controls preload="none" aria-label="Reported clip evidence" style="max-width:100%;max-height:480px" src="/api/admin/reports/${encodeURIComponent(id)}/clip"></video>
            <p>${r.clip_video_expires_at ? `Video retained until ${esc(fmtDate(r.clip_video_expires_at))}.` : 'Video retained until resolution, then for 30 days.'}</p>`}
      </div>` : ''}
      ${screenshotHtml}
      ${r.status === 'new' || r.status === 'reviewed' ? `
      <div class="card admin-card">
        <div class="card-label">TAKE ACTION</div>
        <div class="contact-detail-status-form">
          <select class="settings-input settings-select" id="report-action-select" style="max-width:160px">
            <option value="">Choose action...</option>
            <option value="dismiss">Dismiss</option>
            ${r.clip_evidence ? '<option value="remove_clip">Remove clip</option>' : ''}
            ${r.reported_id ? `<option value="warn">Warn</option>
            <option value="mute">Mute (1hr)</option>
            <option value="ban">Ban (24hr)</option>` : ''}
          </select>
          <input type="text" id="report-action-notes" placeholder="Notes (optional)" class="settings-input" style="flex:1;min-width:120px">
          <button class="btn btn-mode btn-primary" id="report-action-btn">Submit</button>
          <span id="report-action-status" class="admin-status" style="margin:0"></span>
        </div>
      </div>` : ''}
    `;

    document.getElementById('report-action-btn')?.addEventListener('click', async () => {
      const action = document.getElementById('report-action-select')?.value;
      const notes = document.getElementById('report-action-notes')?.value;
      const statusSpan = document.getElementById('report-action-status');
      if (!action) { statusSpan.textContent = 'Select an action'; return; }

      try {
        await api.post(`/api/admin/reports/${id}/${action === 'remove_clip' ? 'remove-clip' : 'resolve'}`, { action, notes });
        statusSpan.textContent = 'Done';
        statusSpan.style.color = 'var(--accent)';
        setTimeout(() => navigate('/admin/reports'), 1000);
      } catch {
        statusSpan.textContent = 'Failed';
        statusSpan.style.color = '#f06060';
      }
    });
  } catch {
    content.innerHTML = '<div class="admin-status">Failed to load report.</div>';
  }
}

export function renderList() {
  if (!_listBound) {
    _listBound = true;
    const filters = document.querySelectorAll('[data-screen="reports"] .report-filter-btn');
    filters.forEach(btn => {
      btn.addEventListener('click', () => {
        filters.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        resetPagination();
        loadReportList(btn.dataset.status);
      });
    });
    document.getElementById('reports-prev')?.addEventListener('click', async () => {
      if (!_cursorHistory.length) return;
      await _navigation.run(async () => {
        const snap = { cursor: _cursor, page: _page, history: [..._cursorHistory] };
        _cursor = _cursorHistory.pop() || null;
        _page--;
        if (!await loadReportList(_activeStatus)) {
          _cursor = snap.cursor; _page = snap.page; _cursorHistory = snap.history;
        }
      });
    });
    document.getElementById('reports-next')?.addEventListener('click', async () => {
      if (!_nextCursor) return;
      await _navigation.run(async () => {
        const snap = { cursor: _cursor, page: _page, history: [..._cursorHistory] };
        _cursorHistory.push(_cursor);
        _cursor = _nextCursor;
        _page++;
        if (!await loadReportList(_activeStatus)) {
          _cursor = snap.cursor; _page = snap.page; _cursorHistory = snap.history;
        }
      });
    });
  }
  resetPagination();
  loadReportList();
}

export function renderDetail(params) {
  loadReportDetail(params.id);
}
